import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    Typography,
    LinearProgress,
    CircularProgress,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BusinessIcon from "@mui/icons-material/Business";
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { getLocalToken } from "../utils";
import axios from "axios";

const api = axios.create({
    baseURL: "/admin/api/v1",
});

api.interceptors.request.use((config) => {
    const token = getLocalToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [animated, setAnimated] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStatistics = async () => {
            try {
                const response = await api.get("/users/statistics");
                setData(response.data.payload);
            } catch (err) {
                console.error("Failed to load statistics", err);
            } finally {
                setLoading(false);
            }
        };
        loadStatistics();
    }, []);

    const stats = useMemo(() => {
        if (!data) {
            return [];
        }

        const social = data.social || {};
        const business = data.business || {};
        const na = data.na || {};
        const paywallNotPassed = data.paywallNotPassed || {};

        return [
            {
                label: "Total",
                value: (data.totalUsers || 0) - ((social.deleted || 0) + (business.deleted || 0) + (na.deleted || 0)),
                deleted: (social.deleted || 0) + (business.deleted || 0) + (na.deleted || 0),
                percent: 100,
                icon: GroupsIcon,
                color: "#6366f1",
            },
            {
                label: "Social",
                value: (social.total || 0) - (social.deleted || 0),
                deleted: social.deleted || 0,
                percent: social.percentage || 0,
                icon: FavoriteIcon,
                color: "#ec4899",
            },
            {
                label: "Business",
                value: (business.total || 0) - (business.deleted || 0),
                deleted: business.deleted || 0,
                percent: business.percentage || 0,
                icon: BusinessIcon,
                color: "#f59e0b",
            },
            {
                label: "Paywall not passed",
                value: (paywallNotPassed.total || 0) - (paywallNotPassed.deleted || 0),
                deleted: paywallNotPassed.deleted || 0,
                percent: paywallNotPassed.percentage || 0,
                icon: MoneyOffIcon,
                color: "#64748b",
            },
            {
                label: "Policy not agreed",
                value: (na.total || 0) - (na.deleted || 0),
                deleted: na.deleted || 0,
                percent: na.percentage || 0,
                icon: GroupsIcon,
                color: "#64748b",
            },
        ];
    }, [data]);

    useEffect(() => {
        if (!stats.length) {
            return;
        }
        const timeout = setTimeout(() => {
            const updated = {};
            stats.forEach((stat) => {
                updated[stat.label] = stat.percent;
            });
            setAnimated(updated);
        }, 150);
        return () => clearTimeout(timeout);
    }, [stats]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 4,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(7, 1fr)",
                },
                gap: 2,
            }}
        >
            {stats.map((stat) => {
                const Icon = stat.icon;
                const animatedValue = animated[stat.label] || 0;

                return (
                    <Card
                        key={stat.label}
                        elevation={0}
                        sx={{
                            p: 1.5,
                            border: "2px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            minHeight: 160,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: 18,
                                    fontWeight: 800,
                                    lineHeight: 1,
                                }}
                            >
                                {stat.label}
                            </Typography>

                            <Icon
                                sx={{
                                    fontSize: 28,
                                    color: stat.color,
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 30,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                }}
                            >
                                {stat.value.toLocaleString()}
                            </Typography>

                            {stat.label !== "Deleted" && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 1,
                                        fontWeight: 500,
                                    }}
                                >
                                    deleted: {stat.deleted.toLocaleString()}
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <LinearProgress
                                variant="determinate"
                                value={animatedValue}
                                sx={{
                                    height: 6,
                                    "& .MuiLinearProgress-bar": {
                                        backgroundColor: stat.color,
                                    },
                                }}
                            />

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    mt: 0.5,
                                    display: "block",
                                }}
                            >
                                {stat.percent.toFixed(1)}% of users
                            </Typography>
                        </Box>
                    </Card>
                );
            })}
        </Box>
    );
}